import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { KnowledgeBaseRequest, KnowledgeBaseResponse } from 'apis/business/types';
import {
  cloneKnowledgeBase,
  createChunk,
  createDataSet,
  createKnowledgeBase,
  deleteChunk,
  deleteDataSet,
  deleteKnowledgeBase,
  getChunk,
  getChunks,
  getDataSet,
  getDataSets,
  getKnowledgeBase,
  getKnowledgeBases,
  getRetrieveChunk,
  importQaChunk,
  updateChunk,
  updateDataSet,
  updateKnowledgeBase
} from './api';
import { queryClient } from 'providers/AllProviders';
import { useNavigate, useParams } from 'react-router-dom';
import { ChunkResponse, DatasetResponse } from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';

export function useGetKnowledgeBases(
  searchText?: string | null,
  options?: UseQueryOptions<{ data: KnowledgeBaseResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: KnowledgeBaseResponse[]; meta: MetaApiResponse }>({
    queryKey: ['knowledgeBases', searchText],
    queryFn: () => getKnowledgeBases(searchText),
    ...options
  });
}

export function useGetKnowledgeBase(options?: UseQueryOptions<KnowledgeBaseResponse, Error>) {
  const { knowledgeBaseId } = useParams<{ locationId: string; knowledgeBaseId: string }>();

  return useQuery<KnowledgeBaseResponse>({
    queryKey: ['knowledgeBase', knowledgeBaseId],
    queryFn: () => getKnowledgeBase(knowledgeBaseId!),
    enabled: Boolean(knowledgeBaseId),
    ...options
  });
}

export function useDeleteKnowledgeBase() {
  return useMutation({
    mutationFn: (id: string) => deleteKnowledgeBase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBases'],
        refetchType: 'active'
      });
    }
  });
}

export function useCreateOrUpdateKnowledgeBase(stopNavigation: boolean = false) {
  const navigate = useNavigate();
  const { knowledgeBaseId } = useParams<{ locationId: string; knowledgeBaseId: string }>();

  return useMutation({
    mutationFn: (payload: KnowledgeBaseRequest) =>
      knowledgeBaseId ? updateKnowledgeBase(knowledgeBaseId!, payload) : createKnowledgeBase(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBases'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBase'],
        refetchType: 'active'
      });
      !stopNavigation && navigate(-1);
    }
  });
}

export function useCloneKnowledgeBase() {
  return useMutation({
    mutationFn: (knowledgeBaseId: number) => cloneKnowledgeBase(knowledgeBaseId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBases'],
        refetchType: 'active'
      });
    }
  });
}

export function useGetDataSets(
  params: any,
  options?: UseQueryOptions<{ data: DatasetResponse[]; meta: MetaApiResponse }, Error>
) {
  const { knowledgeBaseId } = useParams<{ locationId: string; knowledgeBaseId: string }>();

  return useQuery<{ data: DatasetResponse[]; meta: MetaApiResponse }>({
    queryKey: ['datasets', knowledgeBaseId, params],
    queryFn: () => getDataSets(knowledgeBaseId!, params),
    enabled: Boolean(knowledgeBaseId),
    ...options
  });
}

export function useGetDataSet(options?: UseQueryOptions<DatasetResponse, Error>) {
  const { knowledgeBaseId, datasetId } = useParams<{
    knowledgeBaseId: string;
    datasetId: string;
  }>();

  return useQuery<DatasetResponse>({
    queryKey: ['dataset', knowledgeBaseId, datasetId],
    queryFn: () => getDataSet(knowledgeBaseId!, datasetId!),
    enabled: Boolean(knowledgeBaseId && datasetId),
    ...options
  });
}

export function useCreateOrUpdateDataSet(stopNavigation?: boolean) {
  const navigate = useNavigate();
  const { knowledgeBaseId, datasetId } = useParams<{
    knowledgeBaseId: string;
    datasetId: string;
  }>();

  return useMutation({
    mutationFn: (payload: any) =>
      datasetId ? updateDataSet(knowledgeBaseId!, datasetId!, payload) : createDataSet(knowledgeBaseId!, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['datasets', knowledgeBaseId],
        refetchType: 'all'
      });
      if (datasetId) {
        queryClient.invalidateQueries({
          queryKey: ['dataset', knowledgeBaseId, datasetId],
          refetchType: 'all'
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBases'],
        refetchType: 'all'
      });
      queryClient.refetchQueries({
        queryKey: ['agentDatasets']
      });
      if (data !== undefined) {
        !stopNavigation && navigate(-1);
      }
    }
  });
}

export function useUpdateDataSet() {
  const { knowledgeBaseId } = useParams<{
    knowledgeBaseId: string;
  }>();
  return useMutation({
    mutationFn: ({ datasetId, payload }: { datasetId: number; payload: any }) =>
      updateDataSet(knowledgeBaseId!, String(datasetId!), payload),
    onSuccess: (data, variables) => {
      const { datasetId } = variables;
      queryClient.invalidateQueries({
        queryKey: ['datasets', knowledgeBaseId],
        refetchType: 'all'
      });
      if (datasetId) {
        queryClient.invalidateQueries({
          queryKey: ['dataset', knowledgeBaseId, datasetId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useDeleteDataSet() {
  const { knowledgeBaseId } = useParams<{ knowledgeBaseId: string }>();

  return useMutation({
    mutationFn: (id: string) => deleteDataSet(knowledgeBaseId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['datasets', knowledgeBaseId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['knowledgeBases'],
        refetchType: 'active'
      });
    }
  });
}

export function useGetRetrieveChunk(id: any, data: any, options?: UseQueryOptions<any, Error>) {
  return useQuery<any>({
    queryKey: ['retrieveChunk', id, data],
    queryFn: () => getRetrieveChunk(id!, data),
    enabled: Boolean(id && data),
    ...options
  });
}

export function useGetChunks(
  datasetId: any,
  params: any,
  options?: UseQueryOptions<{ data: ChunkResponse[]; meta: MetaApiResponse }, Error>
) {
  const { knowledgeBaseId } = useParams<{
    knowledgeBaseId: string;
  }>();

  return useQuery<{ data: ChunkResponse[]; meta: MetaApiResponse }>({
    queryKey: ['chunks', knowledgeBaseId, datasetId, params],
    queryFn: () => getChunks(knowledgeBaseId!, datasetId!, params),
    enabled: Boolean(knowledgeBaseId && datasetId),
    ...options
  });
}

export function useGetChunk(options?: UseQueryOptions<ChunkResponse, Error>) {
  const { knowledgeBaseId, datasetId, chunkId } = useParams<{
    knowledgeBaseId: string;
    datasetId: string;
    chunkId: string;
  }>();

  return useQuery<ChunkResponse>({
    queryKey: ['chunk', knowledgeBaseId, datasetId, chunkId],
    queryFn: () => getChunk(knowledgeBaseId!, datasetId!, chunkId!),
    enabled: Boolean(knowledgeBaseId && datasetId && chunkId),
    ...options
  });
}

export function useCreateOrUpdateChunk() {
  const navigate = useNavigate();
  const { knowledgeBaseId, datasetId, chunkId } = useParams<{
    knowledgeBaseId: string;
    datasetId: string;
    chunkId: string;
  }>();

  return useMutation({
    mutationFn: (payload: any) =>
      chunkId
        ? updateChunk(knowledgeBaseId!, datasetId!, chunkId!, payload)
        : createChunk(knowledgeBaseId!, datasetId!, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['chunks']
      });
      queryClient.refetchQueries({
        queryKey: ['chunk']
      });
      if (data !== undefined) {
        navigate(-1);
      }
    }
  });
}

export function useDeleteChunk(datasetId: string) {
  const { knowledgeBaseId } = useParams<{ knowledgeBaseId: string }>();

  return useMutation({
    mutationFn: (id: string) => deleteChunk(knowledgeBaseId!, datasetId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['chunks']
      });
    }
  });
}

export function useImportQaChunk(datasetId: string) {
  const { knowledgeBaseId } = useParams<{ knowledgeBaseId: string }>();

  return useMutation({
    mutationFn: (payload: FormData) => importQaChunk(knowledgeBaseId!, datasetId!, payload),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['chunks']
      });
    }
  });
}
